import React, { useCallback, useEffect, useRef, useState } from "react";

import { SideDrawer, useSideDrawer } from "src/agents/base/SideDrawer";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Callout } from "src/design-system/Callout";
import { JsonSchemaUnsavedChangesModal } from "src/jsonSchema/editor/JsonSchemaUnsavedChangesModal";
import { noAllOf, validateJsonSchema } from "src/jsonSchema/validateJsonSchema";

const customValidators = [noAllOf];

export type JsonSchemaEditorConfig = {
  schema: string;
  onSave: (schema: string) => void;
  onDiscard: () => void;
  onDirtyChange: (isDirty: boolean) => void;
};

export const JsonSchemaDrawer: React.FC<{
  config: JsonSchemaEditorConfig | null;
}> = ({ config }) => {
  return (
    <SideDrawer
      dataLoc="json-schema-sidebar"
      drawerId="jsonSchema"
      header={{
        title: "Add JSON schema",
        description: "Paste your JSON schema below",
        closeDataLoc: "close-json-schema-sidebar",
      }}
    >
      {config && <JsonSchemaDrawerContent {...config} />}
    </SideDrawer>
  );
};

/**
 * Content component for the JSON schema drawer.
 * Manages internal editing state and syncs with external schema.
 */
const JsonSchemaDrawerContent: React.FC<JsonSchemaEditorConfig> = ({
  schema: externalSchema,
  onSave,
  onDiscard,
  onDirtyChange,
}) => {
  // Internal state for the editor
  const [internalSchema, setInternalSchema] = useState(externalSchema);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Track if user has manually edited the JSON (not just external sync)
  const [hasUserEdits, setHasUserEdits] = useState(false);
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Use ref to access hasUserEdits in onCloseRequested callback (avoids stale closure)
  const hasUserEditsRef = useRef(hasUserEdits);
  hasUserEditsRef.current = hasUserEdits;

  // Register close handler directly - content component owns the close logic
  const { notifyCloseAccepted, notifyCloseRejected } = useSideDrawer(
    "jsonSchema",
    {
      onCloseRequested: (accept, _reject) => {
        if (hasUserEditsRef.current) {
          setShowConfirmModal(true);
        } else {
          accept(); // No unsaved changes, just close
        }
      },
    },
  );

  // Notify parent of dirty state changes (for UI locking)
  // Only lock when user has made manual edits in the JSON editor
  const prevHasUserEditsRef = useRef(hasUserEdits);
  useEffect(() => {
    if (prevHasUserEditsRef.current !== hasUserEdits) {
      onDirtyChange(hasUserEdits);
      prevHasUserEditsRef.current = hasUserEdits;
    }
  }, [hasUserEdits, onDirtyChange]);

  // UI → JSON Sync: Update internal schema when external changes
  // BUT only if the user hasn't made manual edits
  useEffect(() => {
    if (!hasUserEditsRef.current) {
      setInternalSchema(externalSchema);
      // Validate initial/synced schema
      const error = validateJsonSchema(externalSchema, customValidators);
      setValidationError(error?.message ?? null);
    }
  }, [externalSchema]);

  const handleSave = useCallback(() => {
    const error = validateJsonSchema(internalSchema, customValidators);
    setValidationError(error?.message ?? null);
    if (error) {
      return; // Prevent save if invalid
    }

    onSave(internalSchema);
    setHasUserEdits(false); // Reset after save
    notifyCloseAccepted(); // Close the drawer after saving
  }, [internalSchema, onSave, notifyCloseAccepted]);

  const handleDiscard = useCallback(() => {
    // Reset internal to external (discard user's changes)
    setInternalSchema(externalSchema);
    setValidationError(null);
    setHasUserEdits(false); // Reset after discard
    setShowConfirmModal(false);
    onDiscard();
    notifyCloseAccepted(); // Notify that close was accepted
  }, [externalSchema, onDiscard, notifyCloseAccepted]);

  // "Keep editing" - just close the modal and return to editing
  const handleKeepEditing = useCallback(() => {
    setShowConfirmModal(false);
    notifyCloseRejected(); // Notify that close was rejected
  }, [notifyCloseRejected]);

  const handleChange = useCallback((value: string) => {
    setInternalSchema(value);
    setHasUserEdits(true); // User made a manual edit

    // Validate on change
    const error = validateJsonSchema(value, customValidators);
    setValidationError(error?.message ?? null);
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Validation Error */}
      {validationError && (
        <div className="mx-4 mt-4">
          <Callout type="error">{validationError}</Callout>
        </div>
      )}

      {/* Code Editor */}
      <div className="decideScrollbar flex-1 overflow-auto p-4">
        <CodeEditor
          backgroundColor="white"
          dataLoc="json-schema-editor"
          drawErrorBorder={!!validationError}
          language="jsonschema"
          value={internalSchema}
          autoFocus
          highlightOnFocus
          onChange={handleChange}
        />
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 px-4 py-3">
        <Button
          dataLoc="save-json-schema"
          disabled={!!validationError || !hasUserEdits}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>

      {/* Confirmation Modal */}
      <JsonSchemaUnsavedChangesModal
        canSave={!validationError}
        open={showConfirmModal}
        onDiscard={handleDiscard}
        onKeepEditing={handleKeepEditing}
        onSave={handleSave}
      />
    </div>
  );
};
